/**
 * Karbon360° — UI translation.
 *
 * Albanian is the DEFAULT and English is the alternate, which is the right way
 * round for a product sold in Kosovo: the people entering data every day work in
 * Albanian, and English is what you switch to for a bank or an auditor.
 *
 * Deliberately hand-rolled rather than i18next: the app needs lookup, one
 * interpolation form and a fallback chain, and nothing else. A dependency here
 * would cost more in bundle size than it saves in code.
 *
 * SCOPE — v2 screens only, for now. The v1 screens still routed under /buildings,
 * /recipes, /invoices-new and /emissions/detailed keep their existing mixed
 * strings until they are rebuilt; translating markup that is about to be thrown
 * away is not worth the churn.
 *
 * NOT TRANSLATED, by decision: the PDF report and the CSV export stay English.
 * Those are the artefacts that leave the building and go to lenders, so they
 * hold one stable language until someone asks otherwise.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import sq from './sq';
import en from './en';

const DICTS = { sq, en };
const DEFAULT_LANG = 'sq';
const STORAGE_KEY = 'k360-lang';

export const LANGUAGES = [
  { code: 'sq', label: 'Shqip', short: 'AL' },
  { code: 'en', label: 'English', short: 'EN' }
];

/**
 * Look up `key`, falling back to Albanian and then to the key itself. Returning
 * the key rather than an empty string matters: a missing translation shows up as
 * `records.title` in the UI, which is obvious in review, instead of a blank space
 * that quietly ships.
 */
function translate(lang, key, vars) {
  const dict = DICTS[lang] || DICTS[DEFAULT_LANG];
  let out = dict[key];
  if (out === undefined) out = DICTS[DEFAULT_LANG][key];
  if (out === undefined) {
    if (process.env.NODE_ENV !== 'production') console.warn(`[i18n] missing key: ${key}`);
    return key;
  }
  if (vars) {
    Object.keys(vars).forEach((k) => {
      out = out.split(`{${k}}`).join(String(vars[k]));
    });
  }
  return out;
}

const Ctx = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return DICTS[stored] ? stored : DEFAULT_LANG;
    } catch {
      return DEFAULT_LANG;
    }
  });

  // Keep <html lang> honest — screen readers and browser translation both use it.
  useEffect(() => { document.documentElement.lang = lang; }, [lang]);

  const setLang = useCallback((next) => {
    if (!DICTS[next]) return;
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* private mode */ }
    setLangState(next);
  }, []);

  const t = useCallback((key, vars) => translate(lang, key, vars), [lang]);
  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Degrades to Albanian outside the provider rather than throwing. */
export function useT() {
  const ctx = useContext(Ctx);
  if (ctx) return ctx;
  return {
    lang: DEFAULT_LANG,
    setLang: () => {},
    t: (key, vars) => translate(DEFAULT_LANG, key, vars)
  };
}

export default LanguageProvider;
